import React, { useState, useEffect } from 'react';
import { STARTER_AVATARS } from '../constants/avatars';
import { fetchAvatarSvg } from '../utils';
import '../styles/AvatarSelector.css';

const FALLBACK_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120"><circle cx="60" cy="60" r="50" fill="#d2b48c"/></svg>';

/**
 * Avatar picker grid.
 *
 * @param {object} props
 * @param {string} props.selectedAvatar Filename or SVG markup of the current choice.
 * @param {(filename: string, svgContent: string) => void} props.onAvatarSelect
 * @param {Array<{filename: string, label: string, locked: boolean}>} [props.extraAvatars]
 *   SpudMarket avatars to append after the starters. Locked ones are shown greyed
 *   out and cannot be selected.
 */
const AvatarSelector = ({ selectedAvatar, onAvatarSelect, extraAvatars = [] }) => {
    const [avatarContents, setAvatarContents] = useState({});
    const [loading, setLoading] = useState(true);

    const extraKey = extraAvatars.map((a) => `${a.filename}:${a.locked ? 'locked' : 'owned'}`).join(',');

    useEffect(() => {
        let mounted = true;
        const filenames = [
            ...STARTER_AVATARS.map((a) => a.filename),
            ...extraKey.split(',').filter(Boolean).map((entry) => entry.split(':')[0]),
        ];

        (async () => {
            const contents = {};
            await Promise.all(
                filenames.map(async (filename) => {
                    try {
                        contents[filename] = await fetchAvatarSvg(filename);
                    } catch (error) {
                        console.error(`Failed to load ${filename}:`, error);
                        contents[filename] = FALLBACK_SVG;
                    }
                })
            );
            if (!mounted) return;
            setAvatarContents(contents);
            setLoading(false);
        })();

        return () => { mounted = false; };
    }, [extraKey]);

    const options = [
        ...STARTER_AVATARS.map((a) => ({ ...a, locked: false })),
        ...extraAvatars,
    ];
    const hasLocked = options.some((option) => option.locked);

    const handleAvatarClick = (option) => {
        if (option.locked) return;
        // Pass filename (for registration/auth metadata) and SVG markup (for profile updates).
        onAvatarSelect(option.filename, avatarContents[option.filename]);
    };

    const isSelected = (filename) =>
        selectedAvatar === filename || selectedAvatar === avatarContents[filename];

    if (loading) {
        return (
            <div className="avatar-selector">
                <label>Choose your avatar</label>
                <div className="avatar-grid">
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                        Loading avatars...
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="avatar-selector">
            <label>Choose your avatar</label>
            <div className="avatar-grid">
                {options.map((option) => (
                    <div
                        key={option.filename}
                        className={[
                            'avatar-option',
                            isSelected(option.filename) ? 'selected' : '',
                            option.locked ? 'locked' : '',
                        ].filter(Boolean).join(' ')}
                        title={option.locked ? `${option.label} — unlock in the SpudMarket` : option.label}
                        onClick={() => handleAvatarClick(option)}
                    >
                        <div
                            className="avatar-image"
                            dangerouslySetInnerHTML={{ __html: avatarContents[option.filename] }}
                        />
                        {option.locked && <span className="avatar-option-lock">🔒</span>}
                    </div>
                ))}
            </div>
            {hasLocked && (
                <p className="avatar-selector-note">
                    🔒 Locked avatars are available in the SpudMarket.
                </p>
            )}
        </div>
    );
};

export default AvatarSelector;
